import type { z } from "zod";
import { useCallback, useEffect, useState } from "react";
import ContactlessIcon from "@mui/icons-material/Contactless";
import {
  OrderLineTypes,
  PaymentMethods,
  PaymentStatus,
  Status,
} from "@repo/database";
import { formatIntToCurrency, getTextColor } from "@repo/utils";
import { PaymentSchema } from "@repo/validators";
import { format } from "date-fns";
import Decimal from "decimal.js";
import { RefreshCcwIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { VisuallyHidden } from "react-aria";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useShallow } from "zustand/react/shallow";

import type { Payment } from "~/providers/order-provider";
import type { PaymentMethod } from "~/providers/payment-method-provider";
import type { Terminal } from "~/providers/terminal-provider";
import { useDragScroll } from "~/hooks/use-drag-scroll";
import { useOrder } from "~/hooks/use-order";
import { usePaymentMethod } from "~/hooks/use-payment-method";
import { useTerminal } from "~/hooks/use-terminal";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { eventBus } from "~/lib/eventbus";
import { pay } from "~/lib/pay";
import { cn } from "~/lib/utils";
import { useOrderManager, useOrderUpdates } from "~/managers/use-order-manager";
import { useAlertStore } from "~/stores/use-alert-store";
import { useCartStore } from "~/stores/use-cart-store";
import { useOrderStore } from "~/stores/use-order-store";
import CardPaymentIcon from "../icons/card-payment-icon";
import CashPaymentIcon from "../icons/cash-payment-icon copy";
import PaymentDialog from "../order/payment-dialog";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import CountdownTimerBar from "../ui/countdown-bar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Form, FormField, FormItem, useForm } from "../ui/form";
import { Label } from "../ui/label";
import { ModalInput } from "../ui/modal-input";
import { RegisterButton } from "../ui/register-button";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";

function Payment({
  paymentMethod,
  amountDue,
  orderId,
  terminal,
  onClose,
}: {
  paymentMethod: PaymentMethod;
  amountDue: number;
  orderId: string;
  terminal?: Terminal;
  onClose: () => void;
}) {
  const { t } = useTranslation("common");

  const { showDialog } = useAlertStore(
    useShallow((state) => ({
      showDialog: state.showDialog,
    })),
  );

  const { addPayment, createTerminalPayment, updatePaymentStatus } =
    useOrderManager();

  const setIsLoading = useOrderStore((state) => state.setIsLoading);

  const { orders } = useOrder();

  const { user } = useUser();

  const [terminalPayment, setTerminalPayment] = useState<
    | {
        id?: string;
        cancelUrl?: string;
        statusUrl?: string;
        errorId?: string;
      }
    | undefined
  >();

  const [total, setTotal] = useState(amountDue);

  const [isCancelled, setIsCancelled] = useState(false);

  const [showOverpayWarning, setShowOverpayWarning] = useState(false);

  const [canResend, setCanResend] = useState(false);

  const handleSoftpos = async ({
    id,
    amount,
    paymentMethodId,
    tip,
    type,
  }: {
    id: string;
    amount: number;
    paymentMethodId: string;
    tip: number;
    type: PaymentMethods;
  }) => {
    setIsLoading(true);

    try {
      const result = await addPayment(
        {
          amount,
          tip,
          paymentMethodId,
          change: 0,
          createdAt: new Date(),
          paymentStatus: PaymentStatus.pending,
          id,
        },
        true,
      );
      if (result === true) {
        if (type === PaymentMethods.mollie_t2p) {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              event: "mollie-t2p-payment",
              data: {
                referenceId: id,
                amountValue: new Decimal(amount).toFixed(2).toString(),
                amountCurrency: "EUR",
                description: `Vrodex POS Payment`,
                userId: user.id,
              },
            }),
          );
        } else if (type === PaymentMethods.t2p) {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              event: "softpos",
              data: {
                reference: id,
                amount: {
                  value: new Decimal(amount)
                    .times(100)
                    .toDecimalPlaces(0)
                    .toNumber(),
                  currency: "EUR",
                },
              },
            }),
          );
        }

        onClose();
      } else {
        await showDialog({
          title: "Terminal payment failed",
          message: "Unable to create terminal payment",
          buttons: [
            {
              label: t("close"),
              value: "ok",
              variant: "destructive",
            },
          ],
        });
      }
    } catch (error) {
      console.error(error);
    }

    setIsLoading(false);
  };

  const handleTerminalPayment = async ({
    terminal,
    id,
    amount,
    paymentMethodId,
    tip,
  }: {
    terminal: Terminal;
    id: string;
    amount: number;
    paymentMethodId: string;
    tip: number;
  }) => {
    setIsCancelled(false);
    setIsLoading(true);
    try {
      const result = await addPayment({
        amount,
        tip,
        paymentMethodId,
        change: 0,
        createdAt: new Date(),
        paymentStatus: PaymentStatus.pending,
        id,
      });

      if (result === true) {
        // send payment to terminal
        const response = await createTerminalPayment({
          amount,
          paymentId: id,
          terminal,
        });

        if (response.id) {
          setTerminalPayment(response);
        } else {
          if (response.errorId === "PAY-0") {
            await showDialog({
              title: "Payment error",
              message:
                "Terminal is in use. Press stop on terminal to cancel previous transaction.",
              buttons: [
                {
                  label: t("close"),
                  value: "ok",
                  variant: "destructive",
                },
              ],
            });
          } else {
            await showDialog({
              title: "Payment error",
              message: "Please check if the terminal is online.",
              buttons: [
                {
                  label: t("close"),
                  value: "ok",
                  variant: "destructive",
                },
              ],
            });
          }
        }
      } else {
        await showDialog({
          title: "Payment error",
          message: "Could not create terminal payment.",
          buttons: [
            {
              label: t("close"),
              value: "ok",
              variant: "destructive",
            },
          ],
        });
      }
    } catch (error) {
      console.error(error);
    }

    setIsLoading(false);
  };

  const form = useForm({
    schema: PaymentSchema,
    defaultValues: {
      id: uuidv4(),
      amount: amountDue,
      createdAt: new Date(),
      paymentMethodId: paymentMethod.id,
      paymentMethodName: paymentMethod.name,
      tip: 0,
      change: 0,
    },
  });

  const paymentId = useWatch({ control: form.control, name: "id" });

  const change = useWatch({ control: form.control, name: "change" });

  const tip = useWatch({ control: form.control, name: "tip" });

  const paymentMethodId = useWatch({
    control: form.control,
    name: "paymentMethodId",
  });

  const amount = useWatch({ control: form.control, name: "amount" });

  const handleSubmit = form.handleSubmit(async (data) => {
    if (data.amount === 0) {
      onClose();
    } else {
      if (
        paymentMethod.type === PaymentMethods.t2p ||
        paymentMethod.type === PaymentMethods.mollie_t2p
      ) {
        await handleSoftpos({
          id: data.id,
          amount: data.amount,
          paymentMethodId: paymentMethod.id,
          tip: data.tip,
          type: paymentMethod.type,
        });
      } else {
        if (paymentMethod.type === PaymentMethods.card && !!terminal) {
          await handleTerminalPayment({
            id: data.id,
            amount: data.amount,
            paymentMethodId: paymentMethod.id,
            tip: data.tip,
            terminal,
          });
        } else {
          await handleAddPayment({
            ...data,
            paymentStatus: PaymentStatus.paid,
          });
        }
      }
    }
  });

  const handleAddPayment = async (data: z.infer<typeof PaymentSchema>) => {
    setIsLoading(true);

    const result = await addPayment(data);

    if (result === true) {
      onClose();
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  const cancelTerminalPayment = async () => {
    // form.setValue("id", uuidv4()); // reset the payment id
    setTerminalPayment(undefined);
    if (!terminalPayment?.cancelUrl) return;
    try {
      await pay.cancelTransaction(terminalPayment.cancelUrl);
    } catch (error) {
      console.error(error);
    }
  };

  const handleResendTimer = useCallback(() => {
    const timeoutId: NodeJS.Timeout = setTimeout(() => {
      setCanResend(true);
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    let isSubscribed = true;
    let timeoutId: NodeJS.Timeout | null = null;
    const POLLING_INTERVAL = 1000;

    const getStatus = async () => {
      if (!terminalPayment?.statusUrl) {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        return;
      }

      try {
        const result = await pay.getTransactionStatus(
          terminalPayment.statusUrl,
        );

        if (!isSubscribed) {
          return;
        }

        if (result.timeout) {
          timeoutId = setTimeout(() => {
            getStatus().catch((err) => console.error(err));
          }, POLLING_INTERVAL);
        } else if (result.canceled) {
          setIsCancelled(true);
        } else if (result.paid) {
          // set payment status to paid
          void updatePaymentStatus({
            orderId,
            paymentId,
            paymentStatus: PaymentStatus.paid,
          });
          onClose();
        }
      } catch (error) {
        console.error("Failed to fetch transaction status:", error);
      }
    };

    getStatus().catch((err) => console.error(err));

    return () => {
      isSubscribed = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [onClose, orderId, paymentId, terminalPayment, updatePaymentStatus]);

  useEffect(() => {
    const currentOrder = orders.find((order) => order.id === orderId);

    if (!currentOrder) {
      console.log("order not found");
      return;
    }

    const payment = currentOrder.payments.find(
      (payment) => payment.id === paymentId,
    );

    console.log(`Looking for payment ${paymentId}`);

    if (!payment) {
      console.log("payment not found, payments found on order:");
      console.log(currentOrder.payments);
      return;
    }

    if (payment.paymentStatus === PaymentStatus.canceled) {
      setIsCancelled(true);
    } else if (payment.paymentStatus === PaymentStatus.paid) {
      onClose();
    }
  }, [orders, orderId, paymentId, onClose]);

  useEffect(() => {
    if (isCancelled === true) {
      setCanResend(false);
      handleResendTimer();
    }
  }, [handleResendTimer, isCancelled]);

  return (
    <Form {...form}>
      <form
        id="payment-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <Card>
          <CardContent>
            <div
              className={cn(
                "flex flex-row gap-2",
                paymentMethod.type === PaymentMethods.cash
                  ? "justify-between"
                  : "justify-center",
              )}
            >
              <div className="flex flex-col">
                <span className="text-muted-foreground text-sm">
                  {t("Checkout.total_due")}
                </span>

                <span
                  className={cn(
                    "text-2xl font-semibold ",
                    amountDue > 0 ? "text-red-500" : "text-green-500",
                    paymentMethod.type === PaymentMethods.cash
                      ? "text-left"
                      : "text-center",
                  )}
                >
                  {formatIntToCurrency(amountDue, false)}
                </span>
              </div>
              {paymentMethod.type === PaymentMethods.cash && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-sm">
                    {t("Checkout.change")}
                  </span>
                  <span className="text-2xl font-semibold text-right">
                    {formatIntToCurrency(change, false)}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-4">
              <ModalInput
                label={t("Checkout.pays_with")}
                description={t("Checkout.enter_amount", {
                  payment_method: paymentMethod.name,
                })}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);

                  if (
                    value > amountDue &&
                    paymentMethod.type !== PaymentMethods.cash
                  ) {
                    form.setValue("tip", value - amountDue);
                  } else {
                    if (value > amountDue) {
                      form.setValue("change", value - amountDue);
                    }
                  }
                  setTotal(value);
                }}
                type="decimal"
                className="w-full rounded-none h-full text-right"
              />
            </FormItem>
          )}
        />
        <Separator />
        <FormField
          control={form.control}
          name="tip"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-4">
              <ModalInput
                disabled={paymentMethod.type !== PaymentMethods.cash}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);

                  if (change - value < 0) {
                    form.setValue("change", 0);
                    form.setValue("amount", amount + value);
                  } else {
                    form.setValue("change", change - value);
                  }
                }}
                label={t("Checkout.tip")}
                type="decimal"
                className="w-full rounded-none h-full text-right"
              />
            </FormItem>
          )}
        />
        {paymentMethod.type !== PaymentMethods.cash && (
          <FormItem className="flex flex-col gap-4">
            <ModalInput
              value={total}
              onChange={() => {}}
              disabled
              label={t("Checkout.total")}
              type="decimal"
              className="w-full rounded-none h-full text-right"
            />
          </FormItem>
        )}
      </form>
      <Dialog
        open={!!terminalPayment?.id}
        onOpenChange={(open) => {
          if (open === false) {
            setTerminalPayment(undefined);
          }
        }}
      >
        <DialogContent
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          className="max-w-lg w-full"
        >
          <DialogHeader canClose={false}>
            <DialogTitle>
              {t("Checkout.wait_terminal_payment_title")}
            </DialogTitle>
            <VisuallyHidden>
              <DialogDescription></DialogDescription>
            </VisuallyHidden>
          </DialogHeader>
          <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                {!isCancelled && (
                  <CountdownTimerBar
                    onTimerEnd={() => {
                      setIsCancelled(true);
                    }}
                    duration={44}
                  />
                )}
                {isCancelled && (
                  <p className="text-center text-xl text-red-500 font-bold">
                    {t("Checkout.transaction_canceled")}
                  </p>
                )}
                {!canResend && isCancelled && (
                  <p className="text-center">{t("Checkout.please_wait")}</p>
                )}
                {!isCancelled && (
                  <DialogClose asChild>
                    <Button
                      onClick={() => {
                        void cancelTerminalPayment();
                      }}
                      variant="destructive"
                    >
                      {t("cancel")}
                    </Button>
                  </DialogClose>
                )}
                {isCancelled && terminal && (
                  <>
                    <DialogClose asChild>
                      <Button
                        disabled={!canResend}
                        onClick={() => {
                          void handleTerminalPayment({
                            amount,
                            id: paymentId,
                            paymentMethodId,
                            terminal,
                            tip,
                          });
                        }}
                        variant="brand"
                      >
                        {t("Checkout.send_again")}
                      </Button>
                    </DialogClose>
                    <DialogClose asChild>
                      <Button variant="destructive">{t("close")}</Button>
                    </DialogClose>
                  </>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showOverpayWarning} onOpenChange={setShowOverpayWarning}>
        <DialogContent className="max-w-lg w-full">
          <DialogHeader>
            <DialogTitle>{t("Checkout.overpayment_title")}</DialogTitle>
            <VisuallyHidden>
              <DialogDescription></DialogDescription>
            </VisuallyHidden>
          </DialogHeader>
          <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
            <div className="flex flex-col gap-4">
              <p className="text-center">{t("Checkout.overpayment_message")}</p>
              <Separator />
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => {
                    void handleAddPayment(form.getValues());
                  }}
                >
                  {t("Checkout.overpayment_add_tip")}
                </Button>
                <Button
                  onClick={() => {
                    form.setValue("tip", 0);
                    void handleAddPayment(form.getValues());
                  }}
                >
                  {t("Checkout.overpayment_continue")}
                </Button>
                <DialogClose asChild>
                  <Button
                    onClick={() => {
                      onClose();
                    }}
                    variant="destructive"
                  >
                    {t("cancel")}
                  </Button>
                </DialogClose>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Form>
  );
}

export function CheckoutScreen({ onClose }: { onClose: () => void }) {
  const { paymentMethods, softPosEnabled, mollieT2PEnabled } =
    usePaymentMethod();

  const [hasFinalized, setHasFinalized] = useState(false);

  const { finalizeOrder } = useOrderManager();

  const { handleOrderUpdate } = useOrderUpdates();

  const { items } = useCartStore();

  const total = items
    .filter((item) => item.type !== OrderLineTypes.discount)
    .reduce((acc, curr) => acc + curr.value * curr.quantity, 0);

  const discount = items
    .filter((item) => item.type === OrderLineTypes.discount)
    .reduce((acc, curr) => acc + curr.value * curr.quantity, 0);

  const totalAmount = total - discount;

  const currentOrder = useOrderStore((state) => state.currentOrder);

  const setIsLoading = useOrderStore((state) => state.setIsLoading);

  const isLoading = useOrderStore((state) => state.isLoading);

  const [paymentDialogOpen, setPaymentDialogOpen] = useState<boolean>(false);

  const [terminalSelectOpen, setTerminalSelectOpen] = useState(false);

  const [selectedPayment, setSelectedPayment] = useState<Payment | undefined>();

  const [selectedTerminal, setSelectedTerminal] = useState<
    Terminal | undefined
  >();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethod | undefined
  >();

  const { t } = useTranslation("common");

  const [ref] = useDragScroll();

  const { mutate: updateTerminalPayment, isPending: updatingPayment } =
    api.order.updateTerminalPayment.useMutation();

  const { terminals } = useTerminal();

  const handleFinalize = useCallback(async () => {
    if (hasFinalized) return;

    if (isLoading === true) return;

    try {
      const result = await finalizeOrder();

      if (result === true) {
        setHasFinalized(true);

        onClose();
      }
    } catch (error) {
      setHasFinalized(false);
      console.error(error);
    }
  }, [finalizeOrder, hasFinalized, isLoading, onClose]);

  const handleSelectPaymentMethod = (paymentMethod: PaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    if (paymentMethod.type === PaymentMethods.card) {
      if (terminals.length > 0) {
        setTerminalSelectOpen(true);
      } else {
        setPaymentDialogOpen(true);
      }
    } else {
      setPaymentDialogOpen(true);
    }
  };

  const payments = currentOrder?.payments.filter(
    (payment) =>
      payment.paymentStatus === PaymentStatus.paid ||
      payment.paymentStatus === PaymentStatus.pending,
  );

  const tolerance = 0.001;

  const amountDue = Math.max(
    totalAmount -
      (payments
        ?.filter((payment) => payment.paymentStatus === PaymentStatus.paid)
        .reduce((acc, curr) => acc + curr.amount, 0) ?? 0),
    0,
  );

  const displayAmountDue = amountDue <= tolerance ? 0 : amountDue;

  useEffect(() => {
    const handlePayment = (payload: {
      paymentId: string;
      orderId: string;
      cardholderTicket?: string;
      status?: "PAID" | "CANCEL" | "CHANGE";
    }) => {
      updateTerminalPayment(
        {
          id: payload.paymentId,
          cardholderTicket: payload.cardholderTicket,
          status:
            payload.status === "PAID"
              ? PaymentStatus.paid
              : payload.status === "CANCEL"
                ? PaymentStatus.canceled
                : payload.status === "CHANGE"
                  ? PaymentStatus.canceled
                  : undefined,
        },
        {
          async onSuccess(orderId) {
            await handleOrderUpdate(orderId);
          },
        },
      );
    };

    eventBus.on("softpos-status", handlePayment);

    return () => {
      eventBus.off("softpos-status", handlePayment);
    };
  }, [handleOrderUpdate, updateTerminalPayment]);

  useEffect(() => {
    const handlePayment = () => {
      if (!currentOrder) return;
      void handleOrderUpdate(currentOrder.id);
    };

    eventBus.on("mollie-t2p-result", handlePayment);

    return () => {
      eventBus.off("mollie-t2p-result", handlePayment);
    };
  }, [currentOrder, handleOrderUpdate]);

  if (!currentOrder) {
    return <div>No order selected</div>;
  }

  return (
    <>
      <ScrollArea className="max-h-[75vh] p-4">
        <div className="flex flex-col gap-2 relative @container">
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-4 relative"
            >
              <Button
                onClick={() => {
                  void handleOrderUpdate(currentOrder.id);
                }}
                variant="warning"
              >
                <span className="mr-2">Refresh</span>
                <RefreshCcwIcon className="w-4 h-4" />
              </Button>
              {payments && payments.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Label className="text-center w-full" size="lg">
                    {t("Checkout.payment_history")}
                  </Label>
                  <div
                    ref={ref}
                    className="flex flex-row gap-2 py-2 overflow-auto small-scrollbar"
                  >
                    {payments.map((payment) => (
                      <Button
                        onClick={() => {
                          setSelectedPayment(payment);
                        }}
                        className="flex flex-col gap-1 text-left items-start h-auto w-54"
                        key={payment.id}
                        variant={
                          payment.paymentStatus === PaymentStatus.pending
                            ? "warning"
                            : "success"
                        }
                      >
                        <span>
                          {t("Checkout.payment_date", {
                            date: format(payment.createdAt, "Pp"),
                          })}
                        </span>
                        {payment.employee && (
                          <span>
                            {t("Checkout.employee", {
                              employee: payment.employee,
                            })}
                          </span>
                        )}
                        <span>
                          {t("Checkout.payment_method", {
                            payment_method: payment.paymentMethodName,
                          })}
                        </span>
                        <span>
                          {t("Checkout.amount", {
                            amount: formatIntToCurrency(payment.amount),
                          })}
                        </span>
                        {payment.tip > 0 && (
                          <span>
                            {t("Checkout.tip_amount", {
                              amount: formatIntToCurrency(payment.tip),
                            })}
                          </span>
                        )}
                        {payment.change > 0 && (
                          <span>
                            {t("Checkout.change_amount", {
                              amount: formatIntToCurrency(payment.change),
                            })}
                          </span>
                        )}
                      </Button>
                    ))}
                  </div>
                  <Separator />
                </div>
              )}

              <Card>
                <CardContent>
                  <div className="flex flex-row gap-4 justify-center items-center">
                    <div className="flex flex-col items-center">
                      <span className="text-muted-foreground font-medium text-sm">
                        {t("Checkout.total_due")}
                      </span>
                      <span
                        className={cn(
                          "text-3xl font-bold",
                          amountDue > 0 ? "text-red-500" : "text-green-500",
                        )}
                      >
                        {amountDue <= 0
                          ? formatIntToCurrency(0, true)
                          : formatIntToCurrency(amountDue, true)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {displayAmountDue > 0 && (
                <div className="flex flex-col gap-4">
                  <Card>
                    <CardContent>
                      <div className="grid gap-4 grid-cols-2">
                        {paymentMethods
                          .filter(
                            (paymentMethod) =>
                              paymentMethod.status === Status.active &&
                              paymentMethod.isDefault === true &&
                              (paymentMethod.type !== PaymentMethods.t2p ||
                                softPosEnabled) &&
                              (paymentMethod.type !==
                                PaymentMethods.mollie_t2p ||
                                mollieT2PEnabled),
                          )
                          .sort((a, b) => a.position - b.position)
                          .map((paymentMethod) => (
                            <Button
                              key={paymentMethod.id}
                              onClick={() => {
                                handleSelectPaymentMethod(paymentMethod);
                              }}
                              className="rounded-none relative flex-1 p-2 justify-center items-center text-xl h-24"
                              size="lg"
                              disabled={
                                (paymentMethod.type ===
                                  PaymentMethods.mollie_t2p &&
                                  !mollieT2PEnabled) ||
                                (paymentMethod.type === PaymentMethods.t2p &&
                                  !softPosEnabled) ||
                                isLoading
                              }
                            >
                              <div className="flex flex-col items-center gap-1 flex-1">
                                {paymentMethod.type === PaymentMethods.card && (
                                  <CardPaymentIcon />
                                )}
                                {paymentMethod.type === PaymentMethods.cash && (
                                  <CashPaymentIcon />
                                )}
                                {(paymentMethod.type === PaymentMethods.t2p ||
                                  paymentMethod.type ===
                                    PaymentMethods.mollie_t2p) && (
                                  <ContactlessIcon fontSize="large" />
                                )}
                                <p className="text-xl/tight w-full whitespace-normal break-all line-clamp-2 truncate">
                                  {paymentMethod.name}
                                </p>
                              </div>
                            </Button>
                          ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <Dialog
            open={paymentDialogOpen}
            onOpenChange={(open) => {
              setPaymentDialogOpen(open);
              if (!open) {
                setIsLoading(false);
              }
            }}
          >
            {selectedPaymentMethod && (
              <DialogContent className="max-w-lg w-full">
                <DialogHeader>
                  <DialogTitle>{`${selectedPaymentMethod.name} ${selectedTerminal ? `(${selectedTerminal.name})` : ""}`}</DialogTitle>
                  <VisuallyHidden>
                    <DialogDescription></DialogDescription>
                  </VisuallyHidden>
                </DialogHeader>

                <ScrollArea className="max-h-[80vh] p-4">
                  <Payment
                    orderId={currentOrder.id}
                    amountDue={amountDue}
                    paymentMethod={selectedPaymentMethod}
                    terminal={selectedTerminal}
                    onClose={() => {
                      setPaymentDialogOpen(false);
                    }}
                  />
                </ScrollArea>
                <DialogFooter>
                  {(selectedPaymentMethod.type === PaymentMethods.t2p ||
                    !!selectedTerminal) && (
                    <Button
                      disabled={isLoading || updatingPayment}
                      form="payment-form"
                      type="submit"
                      variant="success"
                      size="lg"
                      className="w-full text-xl"
                    >
                      {t("Checkout.start_transaction")}
                    </Button>
                  )}
                  {selectedPaymentMethod.type !== PaymentMethods.t2p &&
                    !selectedTerminal && (
                      <Button
                        disabled={isLoading}
                        form="payment-form"
                        type="submit"
                        variant="success"
                        size="lg"
                        className="w-full text-xl"
                      >
                        {t("Checkout.add_payment")}
                      </Button>
                    )}
                </DialogFooter>
              </DialogContent>
            )}
          </Dialog>
          <Dialog
            open={terminalSelectOpen}
            onOpenChange={setTerminalSelectOpen}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Terminal selection</DialogTitle>
                <VisuallyHidden>
                  <DialogDescription></DialogDescription>
                </VisuallyHidden>
              </DialogHeader>
              <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar grid grid-cols-1 gap-4">
                {terminals
                  .sort((a, b) => a.position - b.position)
                  .map((terminal) => (
                    <Button
                      style={{
                        backgroundColor: terminal.color ?? "#16a34a",
                        color: getTextColor(terminal.color ?? "#16a34a"),
                      }}
                      key={terminal.terminalId}
                      onClick={() => {
                        setTerminalSelectOpen(false);
                        setPaymentDialogOpen(true);
                        setSelectedTerminal(terminal);
                      }}
                      className="flex flex-col items-center gap-2 h-full"
                    >
                      <span className="text-2xl">{terminal.name}</span>
                    </Button>
                  ))}
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => {
                    setTerminalSelectOpen(false);
                    setPaymentDialogOpen(true);
                  }}
                  className="flex flex-col items-center gap-2 py-2 h-full"
                >
                  <span>{t("Checkout.card_manual")}</span>
                </Button>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button className="w-full" variant="destructive">
                    {t("cancel")}
                  </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <PaymentDialog
            payment={selectedPayment}
            onClose={() => {
              setSelectedPayment(undefined);
            }}
          />
        </div>
      </ScrollArea>

      <div className="p-4 border-t-2 z-10 bg-background shadow">
        <RegisterButton
          onClick={() => {
            if (displayAmountDue > 0) {
              toast.warning(t("Checkout.amount_due_not_zero"), {
                duration: 5000,
              });
              return;
            }
            void handleFinalize();
          }}
          disabled={isLoading === true}
          className="w-full h-full text-xl"
          variant={displayAmountDue > 0 ? "destructive" : "success"}
        >
          {t("Checkout.finalize")}
        </RegisterButton>
      </div>
    </>
  );
}
